"use client";

import { useEffect, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { useRouter } from "next/navigation";
import { getCurrentUser } from "../lib/backend";

const roleDestinations: Record<string, string> = {
  ADMINISTRATOR: "/admin/dashboard",
  PLATFORM_SALESPERSON: "/platform-salesperson/dashboard",
  SALESPERSON: "/platform-salesperson/dashboard",
  ACCOUNT_SALESPERSON: "/account-salesperson/dashboard",
  IT_PERSON: "/it-person/dashboard",
  BOOKKEEPER: "/bookkeeper/dashboard",
  PSE: "/dashboard",
  BSP: "/bsp/dashboard",
  EAH: "/eah/dashboard",
};

const corePlatformRoles = [
  {
    role: "ADMINISTRATOR",
    title: "Administrator",
    description: "Full platform management, accounts, agreements, and revenue distributor controls.",
    badge: "Admin Owner",
    destination: "/admin/dashboard",
  },
  {
    role: "PLATFORM_SALESPERSON",
    title: "Platform Salesperson",
    description: "Core platform participant receiving 20.00% fee distribution from the Admin-owned RevenueDistributor contract.",
    badge: "20.00% Platform Fee Share",
    destination: "/platform-salesperson/dashboard",
  },
  {
    role: "IT_PERSON",
    title: "IT Technician",
    description: "Technical infrastructure status, system activity logs, and system monitoring.",
    badge: "10.00% Platform Fee Share",
    destination: "/it-person/account",
  },
  {
    role: "BOOKKEEPER",
    title: "Bookkeeper",
    description: "Financial accounting, distribution verification, and revenue reporting.",
    badge: "10.00% Platform Fee Share",
    destination: "/bookkeeper/account",
  },
];

export default function LoginPage() {
  const router = useRouter();
  const { ready, authenticated, login, getAccessToken } = usePrivy();
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!ready || !authenticated) {
      return;
    }

    async function redirectByRole() {
      try {
        setRedirecting(true);
        const invitationId = new URLSearchParams(window.location.search).get("inviteId");
        const invitedRole = new URLSearchParams(window.location.search).get("role")?.toUpperCase();

        if (invitationId) {
          if (invitedRole === "BSP" || invitedRole === "EAH") {
            router.replace(`/${invitedRole.toLowerCase()}/onboarding?invitationId=${encodeURIComponent(invitationId)}`);
            return;
          }
          if (invitedRole === "PSE") {
            router.replace(`/pse/onboarding?invitationId=${encodeURIComponent(invitationId)}`);
            return;
          }
          if (invitedRole === "SALESPERSON" || invitedRole === "ACCOUNT_SALESPERSON") {
            router.replace(`/account-salesperson/onboarding?invitationId=${encodeURIComponent(invitationId)}`);
            return;
          }
        }

        const accessToken = await getAccessToken();

        if (!accessToken) {
          throw new Error("Privy access token unavailable.");
        }

        const account = await getCurrentUser(accessToken);
        const role = account.user.platformRole;
        const target = roleDestinations[role] ?? "/my-account";

        router.replace(target);
      } catch (loadError) {
        setRedirecting(false);
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Unable to load your account role."
        );
      }
    }

    void redirectByRole();
  }, [authenticated, getAccessToken, ready, router]);

  if (!ready) {
    return (
      <main className="min-h-screen bg-slate-950 px-6 py-24 text-center text-white">
        <div className="mx-auto max-w-md rounded-3xl border border-slate-800 bg-slate-900 p-8">
          <p className="text-lg text-slate-300">Initializing AGPSIRA Sign-In Portal...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-12 text-white md:px-8 md:py-20">
      <section className="mx-auto max-w-6xl">
        <div className="mb-12 text-center">
          <span className="inline-block rounded-full bg-blue-500/20 px-4 py-1.5 text-xs font-semibold text-blue-300 mb-3">
            AGPSIRA Platform Portal
          </span>
          <h1 className="text-4xl font-bold md:text-6xl">Sign in to AGPSIRA</h1>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-slate-300">
            Welcome to the AGPSIRA platform. Sign in with your registered email address to access your role workspace.
          </p>
        </div>

        {/* Authentication Box */}
        <div className="mb-16 mx-auto max-w-xl rounded-3xl border border-blue-500/30 bg-slate-900/90 p-8 text-center shadow-xl backdrop-blur">
          <h2 className="text-2xl font-bold text-white">Account Authentication</h2>
          <p className="mt-2 text-sm text-slate-300">
            Click below to sign in using your registered email address.
          </p>

          {!authenticated && (
            <button
              type="button"
              onClick={login}
              className="mt-6 w-full rounded-2xl bg-blue-600 py-4 text-lg font-semibold text-white transition hover:bg-blue-500 shadow-lg shadow-blue-600/30"
            >
              Sign In with Email
            </button>
          )}

          {authenticated && redirecting && !error && (
            <div className="mt-6 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4 text-emerald-300 font-medium">
              Authenticated successfully! Verifying platform role and redirecting to your workspace...
            </div>
          )}

          {error && (
            <div className="mt-6 rounded-2xl border border-red-500/50 bg-red-500/10 p-4 text-sm text-red-200">
              {error}
            </div>
          )}
        </div>

        {/* Core Platform Participants Roles Guidance */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">Administrator & Core Platform Roles</h2>
          <p className="text-slate-400 text-sm mb-6">
            When assigned accounts sign in, they will automatically land on their role workspace:
          </p>

          <div className="grid gap-6 md:grid-cols-2">
            {corePlatformRoles.map((item) => (
              <div
                key={item.role}
                className="rounded-3xl border border-slate-800 bg-slate-900/80 p-6 hover:border-slate-700 transition"
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                  <span className="rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">
                    {item.badge}
                  </span>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  {item.description}
                </p>
                <div className="text-xs text-slate-400 font-mono">
                  Destination: <span className="text-blue-400">{item.destination}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

